import "react-datepicker/dist/react-datepicker.css";
import "./date-range-picker-v2.scss";
import React, {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from "react";
import DatePicker from "react-datepicker";
import DateTimeV2, {DATETIME_VIEW} from "../datetime-v2/datetime-v2";
import { useDateTimeFormattingParams } from "../../hooks/use-datetime-formatting-params";
import SelectedValue from "./components/selected-value/selected-value";
import DateRangePickerWrapper from "./components/date-range-picker-wrapper/date-range-picker-wrapper";

export enum DatePickerVariant {
    DATE_RANGE = 'date-range',
    DATE = 'date',
}

export interface IDateRangeISO {

    /** ISO UTC string */
    from?: string,

    /** ISO UTC string */
    to?: string,
}

export interface IDateRange {
    from?: Date,
    to?: Date
}

interface IDateRangePickerProps {
    onChange: (value: IDateRangeISO | null) => void;
    value?: IDateRangeISO,
    placeholder?: React.ReactNode;
    variant?: DatePickerVariant;
    disabled?: boolean;

    /**
     * Optional timezone override IANA name (e.g. 'America/New_York').
     * If not provided, automatically determined via useDateTimeFormattingParams hook
     * (merchant timezone if TIMEZONES feature enabled, otherwise 'UTC').
     */
    timeZone?: string;

    /** Called when the user opens the date picker (clicks on the input). */
    onOpen?: () => void;

    /** Called when the user clicks Apply inside the date picker. */
    onApply?: () => void;
}

const DateRangePickerV2: React.FC<IDateRangePickerProps> = ({
                                                                value,
                                                                placeholder = 'Select Date',
                                                                onChange,
                                                                variant = DatePickerVariant.DATE_RANGE,
                                                                disabled,
                                                                timeZone: timeZoneProp,
                                                                onOpen,
                                                                onApply: onApplyCallback
                                                            }) => {

    /** Auto-determine timezone if not explicitly provided */
    const { timeZone: autoTimeZone, timeFormat, dateFormat } = useDateTimeFormattingParams();
    const timeZone = timeZoneProp ?? autoTimeZone;

    /**
     * Flow summary:
     * - Input `value` is ISO UTC strings (`from`/`to`) coming from backend/store.
     * - We convert ISO UTC -> calendar date (year/month/day) in merchant timezone for UI.
     * - UI selection stores date-only `Date` objects (no time semantics) in component state.
     * - On Apply we convert UI calendar dates -> ISO UTC range boundaries in merchant timezone:
     *   `from` is 00:00:00, `to` is 23:59:59 for the selected calendar day(s).
     * - Output `onChange` always emits ISO UTC strings.
     */
    const [showDateRangePicker, setShowDateRangePicker] = useState(false);
    const [userSpecifiedDateRange, setUserSpecifiedDateRange] = useState<IDateRange>({});
    const inputContainerRef = useRef<HTMLDivElement | null>(null);
    const onClickOutside = useCallback(() => setShowDateRangePicker(false), []);

    /** Extract calendar date components in the given timezone. */
    const getCalendarDateInTimeZone = useCallback((isoUtcString: string) => {
        const parsedDate = new Date(isoUtcString);
        if (isNaN(parsedDate.getTime())) {
            return null;
        }

        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });

        const parts = formatter.formatToParts(parsedDate);
        const yearString = parts.find(part => part.type === 'year')?.value;
        const monthString = parts.find(part => part.type === 'month')?.value;
        const dayString = parts.find(part => part.type === 'day')?.value;

        if (!yearString || !monthString || !dayString) {
            return null;
        }

        return {
            year: parseInt(yearString, 10),
            month: parseInt(monthString, 10) - 1,
            day: parseInt(dayString, 10),
        };
    }, [timeZone]);

    /** Convert ISO UTC value to calendar dates for the picker UI (date-only). */
    const isoDateRangeToUserSpecifiedDateRange = useCallback((isoDateRange: IDateRangeISO): IDateRange | null => {
        if (!isoDateRange.from) {
            return null;
        }

        const fromCalendarDate = getCalendarDateInTimeZone(isoDateRange.from);
        if (!fromCalendarDate) {
            return null;
        }

        const result: IDateRange = {
            from: new Date(fromCalendarDate.year, fromCalendarDate.month, fromCalendarDate.day)
        };

        if (variant === DatePickerVariant.DATE) {
            return result;
        }

        if (!isoDateRange.to) {
            return result;
        }

        const toCalendarDate = getCalendarDateInTimeZone(isoDateRange.to);
        if (!toCalendarDate) {
            return result;
        }

        result.to = new Date(toCalendarDate.year, toCalendarDate.month, toCalendarDate.day);
        return result;
    }, [getCalendarDateInTimeZone, variant]);

    /** Convert a calendar date in merchant timezone to a UTC ISO string. */
    const dateComponentsToUTC = (year: number, month: number, day: number, hour: number, minute: number, second: number): string => {


        const monthStr = (month + 1).toString().padStart(2, '0');
        const dayStr = day.toString().padStart(2, '0');
        const hourStr = hour.toString().padStart(2, '0');
        const minStr = minute.toString().padStart(2, '0');
        const secStr = second.toString().padStart(2, '0');

        const dateStr = `${year}-${monthStr}-${dayStr}T${hourStr}:${minStr}:${secStr}`;

        const utcDate = new Date(`${dateStr}Z`);

        const tzFormatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });

        const tzFormatted = tzFormatter.format(utcDate);

        const [datePart, timePart] = tzFormatted.split(', ');
        const [tzYear, tzMonth, tzDay] = datePart.split('-').map(Number);
        const [tzHour, tzMin, tzSec] = timePart.split(':').map(Number);

        const desiredUTC = Date.UTC(year, month, day, hour, minute, second);
        const actualUTC = Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMin, tzSec);
        const offsetMs = desiredUTC - actualUTC;

        return new Date(utcDate.getTime() + offsetMs).toISOString();
    }

    /** Convert picker UI calendar dates to ISO UTC range (merchant timezone boundaries). */
    const userSpecifiedDateRangeToIsoDateRange = (dateRange: IDateRange): IDateRangeISO => {

        const isoDateRange = {} as IDateRangeISO;

        if (dateRange.from) {
            const year = dateRange.from.getFullYear();
            const month = dateRange.from.getMonth();
            const day = dateRange.from.getDate();

            isoDateRange.from = dateComponentsToUTC(year, month, day, 0, 0, 0);
        }

        if (dateRange.to) {
            const year = dateRange.to.getFullYear();
            const month = dateRange.to.getMonth();
            const day = dateRange.to.getDate();

            isoDateRange.to =  dateComponentsToUTC(year, month, day, 23, 59, 59);
        }

        return isoDateRange;
    }

    /** Update local picker selection. */
    const onDateChange = (dateSelected: Date | null | [Date | null, Date | null] | Date[]) => {

        if (Array.isArray(dateSelected)) {
            const [from, to] = dateSelected;
            if (variant === DatePickerVariant.DATE) {
                setUserSpecifiedDateRange({from: from ?? undefined});
                return;
            }

            setUserSpecifiedDateRange({from: from ?? undefined, to: to ?? undefined});
            return;
        }

        setUserSpecifiedDateRange({from: dateSelected ?? undefined});
    }

    /** Clear selection. */
    const onClearClicked = () => {
        setUserSpecifiedDateRange({});
    }

    /** Apply selection and emit ISO UTC range. */
    const onApplyClicked = () => {
        onApplyCallback?.();
        setShowDateRangePicker(false);

        if (!userSpecifiedDateRange.from) {
            onChange(null);
            return;
        }

        const toDate = variant === DatePickerVariant.DATE_RANGE
            ? (userSpecifiedDateRange.to ?? userSpecifiedDateRange.from)
            : userSpecifiedDateRange.from;

        onChange(userSpecifiedDateRangeToIsoDateRange({
            from: userSpecifiedDateRange.from,
            to: toDate
        }));
    }


    /** Keep UI state in sync with the external ISO UTC `value`. */
    useEffect(() => {
        if (!value) {
            setUserSpecifiedDateRange({});
            return;
        }

        setUserSpecifiedDateRange(isoDateRangeToUserSpecifiedDateRange(value) ?? {});
    }, [value, isoDateRangeToUserSpecifiedDateRange]);


    /** Render preview in browser timezone to match the calendar UI. */
    const getDatePreview = useCallback((value: IDateRange): React.ReactNode => {

        if (!value || (!value.from && !value.to)) {
            return undefined;
        }


        /**
         * Preview is rendered in browser timezone to avoid shifting the calendar day.
         * `value.from/to` here are date-only `Date` objects created for the picker UI.
         */
        const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const previewText: React.ReactNode[] = [];

        if (value.from) {
            previewText.push(
                <DateTimeV2 key={1}
                          utcDateString={value.from.toISOString()}
                          dateTimeView={DATETIME_VIEW.DATE_ONLY}
                          timeZone={browserTimeZone}
                          dateFormat={dateFormat}
                          timeFormat={timeFormat}
                />);
        }

        /** For DATE_RANGE variant, show 'to' date if it differs from 'from' */
        if (variant === DatePickerVariant.DATE_RANGE && value.to && value.to !== value.from) {
            previewText.push(' - ');
            previewText.push(
                <DateTimeV2 key={2}
                          utcDateString={value.to.toISOString()}
                          dateTimeView={DATETIME_VIEW.DATE_ONLY}
                          timeZone={browserTimeZone}
                          dateFormat={dateFormat}
                          timeFormat={timeFormat}
                />);
        }

        return <>{previewText}</>;
    }, [variant, dateFormat, timeFormat]);

    /** Memoized custom input to keep DatePicker stable. */
    const DateRangeInput = useMemo(
        () => forwardRef<HTMLDivElement>((props, ref) => (
            <SelectedValue
                disabled={disabled}
                reference={ref}
                datePreview={getDatePreview(userSpecifiedDateRange) ?? placeholder}
                value={userSpecifiedDateRange}
                onClick={() => {
                    if (!disabled) {
                        const willOpen = !showDateRangePicker;
                        setShowDateRangePicker(willOpen);
                        if (willOpen) onOpen?.();
                    }
                }}
            />
        )),
        [disabled, userSpecifiedDateRange, placeholder, showDateRangePicker, getDatePreview, onOpen]
    );

    const datePickerProps = variant === DatePickerVariant.DATE_RANGE ? {
        disabled,
        monthsShown: 2,
        selectsRange: true as const,
        onChange: onDateChange,
        selected: userSpecifiedDateRange.from,
        startDate: userSpecifiedDateRange.from,
        endDate: userSpecifiedDateRange.to,
        fixedHeight: true,
        onClickOutside,
        disabledKeyboardNavigation: true
    } : {
        disabled,
        monthsShown: 1,
        onChange: onDateChange,
        selected: userSpecifiedDateRange.from,
        fixedHeight: true,
        onClickOutside,
        disabledKeyboardNavigation: true
    };

    return (
        <div className={`date-range-picker-container ${disabled ? 'disabled' : ''}`}>
            <DatePicker
                {...datePickerProps}
                calendarContainer={({children}) => (
                    <DateRangePickerWrapper
                        disabled={disabled}
                        datePreview={getDatePreview(userSpecifiedDateRange)}
                        variant={variant}
                        onClear={onClearClicked}
                        onApply={onApplyClicked}>
                        {children}
                    </DateRangePickerWrapper>
                )}
                portalId="root-portal"
                open={showDateRangePicker}
                customInput={<DateRangeInput ref={inputContainerRef} />}
                popperProps={{placement: 'bottom'}}
                popperModifiers={[
                    {
                        name: "offset",
                        fn: (state) => state,
                        options: {
                            offset: [5, 10],
                        },
                    },
                    {
                        name: "preventOverflow",
                        fn(state: { rects: { floating: { width: number; }; }; x: number; }) {
                            const containerWidth = state.rects.floating.width;


                            if (state.x < 0) {
                                state.x = 0;
                            }


                            if (state.x + containerWidth > window.innerWidth) {
                                state.x = window.innerWidth - containerWidth;
                            }

                            return state;
                        }
                    }
                ]}
            />
        </div>
    );
}

export default DateRangePickerV2;